/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */
/* eslint-disable max-len */
import { DotsHorizontalIcon } from '@radix-ui/react-icons';
import { Archive, Copy, Edit, FileJson, GitForkIcon, Link2Icon, RefreshCcw } from 'lucide-react';
import { ReactNode, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useSWRConfig } from 'swr';

import DisplayError, { formatError } from '../atoms/Error';
import TimelineElement from '../atoms/TimelineElement';
import Toggle from '../atoms/toggle/Toggle';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from '../ui/alert-dialog';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from '../ui/use-toast';

import { Story, TestSuite } from '@/commons/protocols';
import useRevalidation from '@/hooks/useRevalidation';
import { useGet, useMutate } from '@/net/commons';
import { Modal } from '@/pages/projects/Modal';
import { useUserContext } from '@/pages/Root';

// eslint-disable-next-line no-shadow
enum DialogClickItems {
    MOVE = 'move',
    ARCHIVE = 'archive',
    MAINTENANCE = 'maintenance',
    DRAFT = 'draft',
}

type SuiteActionsProps = {
    triggerComponent?: ReactNode;
    projectSlug: string;
    suiteSlug: string;
    storyId: number;
    storySlug?: string;
    storyName?: string;
    archived?: boolean;
    draft?: boolean;
    handleArchive?: () => void;
    handleRun?: () => void;
    handleToggleEnable?: () => void;
    enabled?: boolean;
    maintenance?: boolean;
    submiting?: boolean;
    className?: string;
    fromStoryPage?: boolean;
    block?: boolean;
    stories?: Story[];
    runUrl?: string;
    executionId?: string;
    runId?: string;
};

const SuiteActions = ({
    triggerComponent,
    projectSlug,
    suiteSlug,
    storyId,
    storySlug,
    storyName,
    archived,
    handleArchive,
    handleRun,
    handleToggleEnable,
    enabled,
    draft = false,
    maintenance = false,
    submiting,
    className,
    fromStoryPage = false,
    block = false,
    stories = [],
    runUrl,
    executionId,
    runId,
}: SuiteActionsProps) => {
    const [dialog, setDialog] = useState<DialogClickItems>();
    const triggerRef = useRef<HTMLButtonElement>(null);
    const [selectedSuiteId, setSelectedSuiteId] = useState('');
    const [selectedSuiteName, setSelectedSuiteName] = useState('');
    const [selectedCopyStory, setSelectedCopyStory] = useState(false);
    const { mutate, cache } = useSWRConfig();
    const revalidate = useRevalidation();
    const [localRunModal, setLocalRunModal] = useState<'closed' | 'edit' | 'clone'>('closed');
    const { isAdmin, isImpersonated } = useUserContext().user;
    const hasAdminPermissions = isAdmin || isImpersonated;
    const {
        data: listSuites,
        isLoading: isListLoading,
        error: listSuiteError,
    } = useGet<TestSuite[]>(`/bff/project/${projectSlug}/suites`, {
        revalidate: true,
        method: 'GET',
    });
    const handleValueChange = (value: string) => {
        setSelectedSuiteId(value);
        const selectedSuite = listSuites?.find((suite) => suite.id === parseInt(value));
        if (selectedSuite !== undefined) {
            setSelectedSuiteName(selectedSuite.name);
        }
    };

    const { trigger: triggerEdit } = useMutate(`/bff/project/${projectSlug}/suite/${suiteSlug}/story/:id`, {
        revalidate: true,
        method: 'PATCH',
        onSuccess: () => {
            const keysToUpdate = [
                `/bff/project/${projectSlug}/suite/${suiteSlug}/stories`,
                `/bff/project/${projectSlug}/suite/${suiteSlug}/execution/${executionId}/run/${runId}`,
                `/bff/project/${projectSlug}/suite/${suiteSlug}/story/slug/${storySlug}`,
                `/bff/project/${projectSlug}/suite/${suiteSlug}/executions`,
            ];
            for (const key of keysToUpdate) {
                revalidate(key);
            }
        },
    });

    const { trigger: triggerCopyStory } = useMutate<Story>(`/bff/project/${projectSlug}/suite/${suiteSlug}/story`, {
        revalidate: true,
        method: 'POST',
    });

    const { trigger: triggerMove } = useMutate(`/bff/project/${projectSlug}/suite/${suiteSlug}/story/${storySlug}`, {
        revalidate: true,
        method: 'PUT',
        onSuccess: () => {
            let keyToUpdate = `/bff/project/${projectSlug}/suite/${suiteSlug}/stories`;
            if (block) {
                keyToUpdate = `/bff/project/${projectSlug}/suite/${suiteSlug}/blocks`;
            }
            let dataToUpdate = cache.get(keyToUpdate)?.data;
            if (dataToUpdate) {
                dataToUpdate = dataToUpdate.filter((story: Story) => story.id !== storyId);
                mutate(keyToUpdate, dataToUpdate);
            }
        },
    });

    const handleMove = async () => {
        const story = stories.find((s) => s.id === storyId);
        if (!story || !selectedSuiteId) {
            toast({
                title: 'No story or suite selected',
                variant: 'destructive',
            });
            return;
        }
        try {
            if (selectedCopyStory) {
                await triggerCopyStory({
                    name: story.name,
                    kind: story.kind,
                    ticket_url: story.ticketUrl,
                    steps: story.steps,
                    entryHref: story.entryHref,
                    browsers: story.browsers,
                    featureFlags: story.featureFlags,
                    newTestSuiteId: parseInt(selectedSuiteId),
                });
                toast({
                    title: `copied ${block ? 'block' : 'story'} ${story.name} in ${selectedSuiteName}`,
                });
            } else {
                await triggerMove({
                    name: story.name,
                    kind: story.kind,
                    ticket_url: story.ticketUrl,
                    steps: story.steps,
                    entryHref: story.entryHref,
                    browsers: story.browsers,
                    featureFlags: story.featureFlags,
                    newTestSuiteId: parseInt(selectedSuiteId),
                });
                toast({
                    title: `Moved ${block ? 'block' : 'story'} ${story.name} to ${selectedSuiteName}`,
                });
            }
        } catch (err) {
            toast({
                title: `Error moving ${block ? 'block' : 'story'}`,
                description: formatError(err),
                variant: 'destructive',
            });
        }
    };

    const toggleMaintenanceMode = () => {
        try {
            triggerEdit({
                idParam: storyId,
                maintenance: !maintenance,
            });
            toast({
                title: `Maintenance mode ${maintenance ? 'disabled' : 'enabled'}`,
            });
        } catch (err) {
            toast({
                title: 'Error while changing the maintenance status',
                description: formatError(err),
                variant: 'destructive',
            });
        }
    };

    const handlePublishDraft = () => {
        try {
            triggerEdit({
                idParam: storyId,
                draft: false,
            });
            toast({
                title: `Published story ${storyName}`,
            });
        } catch (err) {
            toast({
                title: `Error publishing story ${storyName}`,
                description: formatError(err),
                variant: 'destructive',
            });
        }
    };

    return (
        <>
            <AlertDialog>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        {triggerComponent || (
                            <Button
                                ref={triggerRef}
                                variant="secondary"
                                className={`flex h-8 w-8 p-0 data-[state=open]:bg-muted ${className} bg-white`}
                                disabled={submiting}
                            >
                                <DotsHorizontalIcon className="h-4 w-4" />
                                <span className="sr-only">
                                    Open the story or block options menu for story {storyName}
                                </span>
                            </Button>
                        )}
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                        {runUrl && (
                            <>
                                <DropdownMenuItem asChild>
                                    <Link className="cursor-pointer" to={runUrl}>
                                        <div className="flex w-full justify-between items-center">
                                            Open run
                                            <Link2Icon size={14} className="text-gray-500" />
                                        </div>
                                    </Link>
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                            </>
                        )}
                        {handleRun && !block && archived === false && (
                            <>
                                <DropdownMenuItem role="button" onClick={handleRun} className="justify-between">
                                    Run again
                                    <RefreshCcw className=" text-gray-500" size={14} />
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                            </>
                        )}
                        {handleToggleEnable && archived === false && (
                            <>
                                <DropdownMenuItem asChild>
                                    <div className="justify-between">
                                        <span>{enabled || enabled === undefined ? 'Disable' : 'Enable'}</span>
                                        <Toggle
                                            defaultValue={enabled === undefined ? true : enabled}
                                            onClick={() => {
                                                handleToggleEnable();
                                            }}
                                        />
                                    </div>
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                            </>
                        )}
                        {hasAdminPermissions && archived === false && (
                            <>
                                <DropdownMenuItem asChild>
                                    <Link
                                        className="cursor-pointer"
                                        to={`/story/${projectSlug}/${suiteSlug}/${storySlug}/edit-live`}
                                    >
                                        <div className="flex w-full justify-between items-center">
                                            Edit
                                            <Edit size={14} className="text-gray-400" />
                                        </div>
                                    </Link>
                                </DropdownMenuItem>
                            </>
                        )}
                        {hasAdminPermissions && archived === false && (
                            <>
                                <DropdownMenuItem asChild>
                                    <Link
                                        className="cursor-pointer"
                                        to={`/story/${projectSlug}/${suiteSlug}/live/${storySlug}`}
                                    >
                                        <div className="flex w-full justify-between items-center">
                                            Clone
                                            <GitForkIcon size={14} className="text-gray-400" />
                                        </div>
                                    </Link>
                                </DropdownMenuItem>
                            </>
                        )}
                        {hasAdminPermissions && fromStoryPage === true && (
                            <DropdownMenuItem asChild>
                                <AlertDialogTrigger
                                    className="w-full cursor-pointer"
                                    onClick={() => {
                                        setDialog(DialogClickItems.MOVE);
                                    }}
                                >
                                    Move
                                </AlertDialogTrigger>
                            </DropdownMenuItem>
                        )}
                        <DropdownMenuItem asChild>
                            <Link
                                className="cursor-pointer"
                                to={
                                    archived
                                        ? `/story/${projectSlug}/${suiteSlug}/${storySlug}/${storyId}`
                                        : `/story/${projectSlug}/${suiteSlug}/${storySlug}`
                                }
                            >
                                <div className="flex w-full justify-between items-center">
                                    View json
                                    <FileJson size={14} className="text-gray-400" />
                                </div>
                            </Link>
                        </DropdownMenuItem>
                        {hasAdminPermissions && (
                            <>
                                <DropdownMenuSeparator />
                                {archived === false && (
                                    <>
                                        <DropdownMenuItem
                                            asChild
                                            role="button"
                                            onClick={() => {
                                                setLocalRunModal('edit');
                                            }}
                                        >
                                            <div className="flex w-full justify-between items-center">
                                                Edit locally
                                                <Edit size={14} className="text-gray-400" />
                                            </div>
                                        </DropdownMenuItem>
                                        <DropdownMenuItem
                                            asChild
                                            role="button"
                                            onClick={() => {
                                                setLocalRunModal('clone');
                                            }}
                                        >
                                            <div className="flex w-full justify-between items-center">
                                                Clone locally
                                                <Edit size={14} className="text-gray-400" />
                                            </div>
                                        </DropdownMenuItem>
                                        <DropdownMenuSeparator />
                                    </>
                                )}
                                {enabled && (
                                    <>
                                        {draft && hasAdminPermissions && (
                                            <DropdownMenuItem asChild>
                                                <AlertDialogTrigger
                                                    className="w-full cursor-pointer"
                                                    onClick={() => {
                                                        setDialog(DialogClickItems.DRAFT);
                                                    }}
                                                >
                                                    <div className="flex w-full justify-between items-center">
                                                        Publish draft
                                                    </div>
                                                </AlertDialogTrigger>
                                            </DropdownMenuItem>
                                        )}
                                        <DropdownMenuItem asChild>
                                            <AlertDialogTrigger
                                                className="w-full cursor-pointer"
                                                onClick={() => {
                                                    setDialog(DialogClickItems.MAINTENANCE);
                                                }}
                                            >
                                                <div className="flex w-full justify-between items-center">
                                                    {maintenance ? 'Exit' : 'Enter'} maintenance
                                                </div>
                                            </AlertDialogTrigger>
                                        </DropdownMenuItem>
                                    </>
                                )}
                                {archived === false && (
                                    <>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem asChild>
                                            <AlertDialogTrigger
                                                className="w-full cursor-pointer"
                                                onClick={() => {
                                                    setDialog(DialogClickItems.ARCHIVE);
                                                }}
                                            >
                                                <div className="flex w-full justify-between items-center">
                                                    Archive
                                                    <Archive size={14} className="text-gray-400" />
                                                </div>
                                            </AlertDialogTrigger>
                                        </DropdownMenuItem>
                                    </>
                                )}
                            </>
                        )}
                    </DropdownMenuContent>
                </DropdownMenu>
                <AlertDialogContent
                    onCloseAutoFocus={(event) => {
                        event.preventDefault();
                        triggerRef?.current?.focus();
                    }}
                >
                    {dialog === DialogClickItems.ARCHIVE ? (
                        <div>
                            <AlertDialogHeader>
                                <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                                <AlertDialogDescription>
                                    This will archive the story and hide it from the Suite. You won't be able to run
                                    this story anymore.
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter className="mt-4">
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleArchive?.()}>Continue</AlertDialogAction>
                            </AlertDialogFooter>
                        </div>
                    ) : dialog === DialogClickItems.MAINTENANCE ? (
                        <div>
                            <AlertDialogHeader>
                                <AlertDialogTitle>
                                    {maintenance ? 'Exit Maintenance Mode' : 'Enter Maintenance Mode'}
                                </AlertDialogTitle>
                                <AlertDialogDescription>
                                    {maintenance
                                        ? 'This will resume notifications when the story runs finish.'
                                        : // eslint-disable-next-line max-len
                                          'When this story is run it will no longer notify users when runs finish, reducing noise. This will also cause the story not to run in CI.'}
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter className="mt-4">
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={() => toggleMaintenanceMode()}>Continue</AlertDialogAction>
                            </AlertDialogFooter>
                        </div>
                    ) : dialog === DialogClickItems.DRAFT ? (
                        <div>
                            <AlertDialogHeader>
                                <AlertDialogTitle>Publish draft?</AlertDialogTitle>
                                <AlertDialogDescription>
                                    If you publish the draft, the story will start running in CI and will send
                                    notifications when runs finish. You cannot re-enter draft status. If you want to
                                    disable notifications, you can enter maintenance mode.
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter className="mt-4">
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handlePublishDraft()}>Continue</AlertDialogAction>
                            </AlertDialogFooter>
                        </div>
                    ) : dialog === DialogClickItems.MOVE ? (
                        <div>
                            <AlertDialogHeader>
                                <AlertDialogTitle>Moving to Another Test Suite</AlertDialogTitle>
                                <AlertDialogDescription>
                                    Select the test suite you want to move the story to:
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <Select value={selectedSuiteId} onValueChange={handleValueChange}>
                                <SelectTrigger>
                                    <SelectValue>
                                        {selectedSuiteId && listSuites
                                            ? listSuites.find(
                                                  (eachSuite) => eachSuite.id.toString() === selectedSuiteId,
                                              )?.name
                                            : 'Select a Suite'}
                                    </SelectValue>
                                </SelectTrigger>
                                <SelectContent className="max-h-[200px] sm:max-h-[600px]">
                                    {isListLoading ? (
                                        'Loading...'
                                    ) : listSuiteError ? (
                                        <DisplayError error={listSuiteError} />
                                    ) : (
                                        listSuites &&
                                        listSuites
                                            .filter((eachSuite) => eachSuite.slug !== suiteSlug)
                                            .map((eachSuite) => (
                                                <SelectItem
                                                    key={eachSuite.id}
                                                    value={eachSuite.id.toString()}
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    {eachSuite.name}
                                                </SelectItem>
                                            ))
                                    )}
                                </SelectContent>
                            </Select>
                            <div className="flex items-center mt-4">
                                <Checkbox
                                    onClick={() => {
                                        setSelectedCopyStory(!selectedCopyStory);
                                    }}
                                />
                                <label className="ml-2">Keep a copy of the story in the target suite</label>
                            </div>
                            <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleMove()}>Move</AlertDialogAction>
                            </AlertDialogFooter>
                        </div>
                    ) : null}
                </AlertDialogContent>
            </AlertDialog>
            {localRunModal !== 'closed' && (
                <Modal
                    isOpen={true}
                    onClose={() => {
                        setLocalRunModal('closed');
                    }}
                    title={`${localRunModal === 'edit' ? 'Edit' : 'Clone'} in a local test browser`}
                >
                    <div className="sm:w-[800px] sm:h-[300px] max-w-full">
                        <TimelineElement title={'Install the heal-dev CLI'}>
                            <div className="flex flex-row items-center gap-2">
                                <code className="w-min whitespace-nowrap">npm i heal-dev</code>
                                <button
                                    title="Copy GitHub Action snippet to clipboard"
                                    onClick={() => {
                                        toast({
                                            title: 'Copied to clipboard',
                                            duration: 5000,
                                        });
                                        navigator.clipboard.writeText('npm i heal-dev');
                                    }}
                                    className=" px-2 py-1 hover:bg-purple-100 rounded"
                                >
                                    <Copy size={14} color="rgb(168 85 247)" />
                                </button>
                            </div>
                        </TimelineElement>
                        <TimelineElement title={'Set the HEAL_API_KEY env var'}>
                            <div>
                                Create a{' '}
                                <Link to={`/organisation/keys`} target="_blank" className="underline">
                                    Heal API key
                                </Link>
                                , and add it to your local environment
                            </div>
                            <div className="flex flex-row items-center gap-2 mt-2">
                                <code className="space-y-12">export HEAL_API_KEY=YOUR_KEY</code>
                                <button
                                    title="Copy GitHub Action snippet to clipboard"
                                    onClick={() => {
                                        toast({
                                            title: 'Copied to clipboard',
                                            duration: 5000,
                                        });
                                        navigator.clipboard.writeText('export HEAL_API_KEY=YOUR_KEY');
                                    }}
                                    className=" px-2 py-1 hover:bg-purple-100 rounded"
                                >
                                    <Copy size={14} color="rgb(168 85 247)" />
                                </button>
                            </div>
                        </TimelineElement>
                        <TimelineElement title={'Start the test studio with a local browser'} hideTimeline={true}>
                            <div className="flex flex-row items-center gap-2 mt-2">
                                <code className="space-y-12">
                                    npx heal-dev {localRunModal} {projectSlug}/{suiteSlug}/{storySlug}
                                </code>
                                <button
                                    title="Copy GitHub Action snippet to clipboard"
                                    onClick={() => {
                                        toast({
                                            title: 'Copied to clipboard',
                                            duration: 5000,
                                        });
                                        navigator.clipboard.writeText(
                                            `npx heal-dev ${localRunModal} ${projectSlug}/${suiteSlug}/${storySlug}`,
                                        );
                                    }}
                                    className=" px-2 py-1 hover:bg-purple-100 rounded"
                                >
                                    <Copy size={14} color="rgb(168 85 247)" />
                                </button>
                            </div>
                        </TimelineElement>
                    </div>
                </Modal>
            )}
        </>
    );
};

export default SuiteActions;
